import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { AlertTriangle, Bomb } from 'lucide-react';

const REQUIRED_PHRASE = 'WIPE-ALL-UPLOADED-DATA';

/**
 * WipeAllUploadedButton — global nuclear option to start from a clean slate.
 *
 * Three explicit scopes:
 *   • csv    — wipes CSV / manual / Excel uploaded transactions across every
 *              investor (recorded / uploaded side). Leaves Investec bank
 *              rows intact.
 *   • bank   — wipes historical + staging Investec bank rows. Leaves CSV
 *              uploaded transactions intact.
 *   • both   — everything: CSVs + bank + staging + allocations + audit + batches.
 *
 * Preserved regardless of scope: expected_cashflows (Excel RP/LB loanbook),
 * users, loans, RP data, live investec_sync rows unless "bank" or "both".
 *
 * Admin must type "WIPE-ALL-UPLOADED-DATA" exactly to confirm.
 */
export function WipeAllUploadedButton({ API_URL, headers, onComplete }) {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [mode, setMode] = useState('both'); // 'csv' | 'bank' | 'both'
  const [busy, setBusy] = useState(false);

  const ready = phrase === REQUIRED_PHRASE;

  const handleConfirm = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/wipe-all-uploaded`,
        { confirm: REQUIRED_PHRASE, mode },
        { headers }
      );
      toast.success(r.data?.message || 'Wipe complete.');
      setOpen(false);
      setPhrase('');
      onComplete && onComplete(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe-all failed');
    } finally {
      setBusy(false);
    }
  };

  const modeDescription = {
    csv: 'ONLY the CSV / manual / Excel uploaded transactions across every investor. Investec bank rows will be left alone.',
    bank: 'ONLY the Investec bank data — historical CSV + staging + committed sync rows + all bank allocations. CSV/uploaded transactions will be left alone.',
    both: 'EVERYTHING — every uploaded CSV, every Investec bank row (historical + sync + staging), all allocations, all audit trails, all batch headers.',
  }[mode];

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPhrase(''); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="destructive"
          data-testid="wipe-all-uploaded-button"
          className="bg-red-700 hover:bg-red-800"
        >
          <Bomb className="h-3.5 w-3.5 mr-1" />
          Wipe ALL Uploaded Data
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="wipe-all-uploaded-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> Nuclear Wipe — Confirm
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-3">
            <span className="block">
              Pick what to wipe. <strong>This cannot be undone.</strong>
            </span>
            <span className="block text-sm">
              <strong>Preserved regardless of scope:</strong> the Excel loanbook
              (expected_cashflows), users, loans, risk participations.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <RadioGroup value={mode} onValueChange={setMode} className="space-y-2" data-testid="wipe-all-mode">
            <div className="flex items-start gap-2 border rounded p-2 hover:bg-slate-50">
              <RadioGroupItem value="csv" id="wipe-mode-csv" data-testid="wipe-mode-csv" className="mt-0.5" />
              <Label htmlFor="wipe-mode-csv" className="cursor-pointer flex-1">
                <div className="font-semibold text-sm">CSV / uploaded transactions only</div>
                <div className="text-xs text-slate-500">Wipes csv_import, excel_import, manual and any non-bank source. Bank rows preserved.</div>
              </Label>
            </div>
            <div className="flex items-start gap-2 border rounded p-2 hover:bg-slate-50">
              <RadioGroupItem value="bank" id="wipe-mode-bank" data-testid="wipe-mode-bank" className="mt-0.5" />
              <Label htmlFor="wipe-mode-bank" className="cursor-pointer flex-1">
                <div className="font-semibold text-sm">Investec bank data only</div>
                <div className="text-xs text-slate-500">Wipes investec_sync, investec_api, investec_historical + all staging bank rows + bank allocations. CSV uploads preserved.</div>
              </Label>
            </div>
            <div className="flex items-start gap-2 border rounded p-2 hover:bg-slate-50">
              <RadioGroupItem value="both" id="wipe-mode-both" data-testid="wipe-mode-both" className="mt-0.5" />
              <Label htmlFor="wipe-mode-both" className="cursor-pointer flex-1">
                <div className="font-semibold text-sm">Both — everything uploaded/synced</div>
                <div className="text-xs text-slate-500">All CSV uploads + all bank rows + all allocations + staging + audit + batch headers.</div>
              </Label>
            </div>
          </RadioGroup>
          <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2">
            {modeDescription}
          </div>
          <div className="text-sm">
            Type{' '}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-700">
              {REQUIRED_PHRASE}
            </code>{' '}
            to confirm:
          </div>
          <Input
            placeholder={REQUIRED_PHRASE}
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            className={`font-mono ${ready ? 'border-red-600' : ''}`}
            data-testid="wipe-all-confirm-input"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!ready || busy}
            onClick={handleConfirm}
            data-testid="wipe-all-confirm-button"
            className="bg-red-700 hover:bg-red-800"
          >
            {busy ? 'Wiping…' : `WIPE ${mode === 'csv' ? 'CSV UPLOADS' : mode === 'bank' ? 'BANK DATA' : 'EVERYTHING'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
